import React, { useState, useEffect } from 'react';
import { LiveKitRoom } from '@livekit/components-react';
import { TextChat } from './TextChat';
import { ArrowLeft, MessageSquare, MessageSquareMore, Volume2, Images, Menu } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import LoginPopup from '@/app/popup/login-popup';
import { AdvancedImage } from '@cloudinary/react';

import { useProfileImage, useCharacterData, useChatStatus, useGlobalChatState, useGlobalLiveKitRoomState } from '../../../hooks/useGlobalChatState';
import { LiveImageStatus } from '../../../data/liveMessageData/liveMessage';
import { ChatStatus } from '../../../data/globalChatState';
import ConnectionIssuePopup from '@/app/popup/ConnectionIssuePopup';
import ProfilePanel from './ProfilePanel';
import AudioUnlocker from '@/app/components/AudioUnlocker';

interface ChatLayoutProps {
  children: React.ReactNode;
}

export function ChatLayout({ children }: ChatLayoutProps) {
  // Get profile image from global state
  const { loadingStatus: profileImageStatus, imageData: profileImageData } = useProfileImage();

  return (
    <div className={`flex flex-row justify-center w-full h-full relative`}>
      {/* Content overlay */}
      <div className="w-full h-full relative z-10">
        <main className="flex flex-col w-full h-full">
          <div 
            className="flex flex-row w-full h-full gap-0 relative lg:max-w-[1200px]" 
            style={{ backgroundColor: 'transparent' }}
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}



// Unified Chat Panel Components
interface UnifiedChatPanelProps {
  // Callbacks
  onStartChat?: () => void;
  initiateVideoRoom?: () => Promise<void>;
}


interface ProfileHeaderProps {
  onToggleInfoPanel: () => void;
  onToggleGallery?: () => void;
}

function ProfileHeader({ onToggleInfoPanel, onToggleGallery }: ProfileHeaderProps) {
  const router = useRouter();
  const [globalState] = useGlobalChatState();
  const { chatStatus, setChatStatus } = useChatStatus();
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  
  // Get data from global state
  const { character: character } = useCharacterData();
  const { loadingStatus: profileImageStatus, imageData: profileImageData } = useProfileImage();
  
  const cloudinaryImage = profileImageStatus === LiveImageStatus.LOADED ? profileImageData : undefined;

  const handleBack = () => {
    // leave chat
    if (globalLKRoomState.room && globalLKRoomState.room.state === 'connected') {
      globalLKRoomState.room.disconnect();
      setChatStatus(ChatStatus.INFO);
    }
    // go back via navigation
    if (typeof window !== 'undefined' && window.history.length > 1 &&
      // if the referrer is the same domain, go back ( i.e don't go back to google login)
      document.referrer.includes(window.location.hostname)
    ) {
      router.back();
    }
    else {
      // if no history to go back to, redirect to my-chat
      router.push('/my-chat');
    }
  };

  // Don't render if no avatar data
  if (!character) {
    return (
      <div className="flex items-center justify-between p-4 border-b border-white/20 flex-shrink-0">
        <div className="text-white/60 text-sm">Loading character...</div>
      </div>
    );
  }



  return (
    <div className="flex items-center justify-between pt-4 px-4 flex-shrink-0">
      {/* Left side - Back button and Profile button */}
      <div className="flex items-center gap-2">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center justify-center w-10 h-10 bg-black/40 hover:bg-black/50 rounded-full transition-colors"
          title="Go back"
        >
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>

        {/* Profile Button (pic + name) */}
        <button
          onClick={() => setShowProfilePanel(true)}
          className="flex items-center gap-2 px-3 h-10 bg-black/40 hover:bg-black/50 rounded-full transition-colors"
        >
          <span className="text-sm font-medium text-white drop-shadow-lg truncate max-w-[120px]">
            {character.metadata.avatar_name || 'Unknown Avatar'}
          </span>
        </button>
      </div>

      {/* Right side - Chat History toggle */}
      <div className="flex items-center gap-2">
          {/* Gallery button */}
          <button
            onClick={onToggleGallery}
            className="flex items-center justify-center w-10 h-10 bg-black/40 hover:bg-black/50 rounded-full transition-colors"
            title="Image Gallery"
          >
            <Images className="w-5 h-5 text-white" />
          </button>

          {/* Menu Button */}
          <button
            onClick={() => setShowProfilePanel(true)}
            className="flex items-center justify-center w-10 h-10 bg-black/40 hover:bg-black/50 rounded-full transition-colors"
            title="Menu"
          >
            <Menu className="w-5 h-5 text-white" />
          </button>
      </div>

      {/* Profile Panel */}
      <ProfilePanel
        isOpen={showProfilePanel}
        onClose={() => setShowProfilePanel(false)}
      />
    </div>
  );
}



interface ChatMessagesContainerProps {
  onStartChat?: () => void;
  isGalleryOpen: boolean;
  onToggleGallery: () => void;
}

function ChatMessagesContainer({
  onStartChat,
  isGalleryOpen,
  onToggleGallery,
}: ChatMessagesContainerProps) {  
  // Get data from global state
  const [globalState, globalActions] = useGlobalChatState();
  const { character: avatar, chatStatus: chatState } = globalState;
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  
  const avatarId = avatar?.metadata.avatar_id || '';
  const hasHistory = avatar?.chatHistory?.hasHistory || false;
  
  // Inactivity timeout state
  const [isInactivityTimeoutPopupOpen, setIsInactivityTimeoutPopupOpen] = React.useState(false);
  const [inactivityTimeoutReason, setInactivityTimeoutReason] = React.useState<'server_signal' | 'user_inactivity' | null>(null);
  
  // React to inactivity timeout state changes from global state
  React.useEffect(() => {
    const checkInactivityTimeout = () => {
      const timeoutState = globalState.isInactivityTimeoutPopupOpen;
      if (timeoutState && !isInactivityTimeoutPopupOpen) {
        setIsInactivityTimeoutPopupOpen(true);
        setInactivityTimeoutReason(globalState.inactivityTimeoutReason);
      }
    };
    
    checkInactivityTimeout();
  }, [globalState.isInactivityTimeoutPopupOpen, globalState.inactivityTimeoutReason, isInactivityTimeoutPopupOpen]);
  
  // Handle inactivity timeout popup close
  const handleInactivityTimeoutClose = () => {
    setIsInactivityTimeoutPopupOpen(false);
    setInactivityTimeoutReason(null);
    
    // Also reset the global state
    globalActions.setInactivityTimeoutState({
      isInactivityTimeoutPopupOpen: false,
      inactivityTimeoutReason: null
    });
  };
  


  // Always show the TextChat component immediately, don't block on history loading
  // Show a small loading indicator inside the chat area if history is still loading
  
  // Don't render if no avatar data
  if (!avatar) {
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center p-4">
        <span className="text-white/60 text-sm">Loading character...</span>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <TextChat
        onStartChat={onStartChat}
        showResizeHandle={false}
        isGalleryOpen={isGalleryOpen}
        onToggleGallery={onToggleGallery}
      />
      
      {/* Inactivity Timeout Popup */}
      <ConnectionIssuePopup
        isOpen={isInactivityTimeoutPopupOpen}
        onClose={handleInactivityTimeoutClose}
        type="inactivity_timeout"
        inactivityReason={inactivityTimeoutReason}
      />
    </div>
  );
}

export function UnifiedChatPanel({
  initiateVideoRoom,
}: UnifiedChatPanelProps) {
  const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  
  // Get data from global state
  const [globalState] = useGlobalChatState();
  const { character: avatar, chatStatus: chatState, } = globalState;
  const [, globalActions] = useGlobalChatState();
  const { setChatStatus } = useChatStatus();
  const { session } = useAuth();
  
  const avatarId = avatar?.metadata.avatar_id || '';
  const hasHistory = avatar?.chatHistory?.hasHistory || false;

  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [loginRedirectUrl, setLoginRedirectUrl] = useState('/');



  // Close panel when clicking outside (keeping for potential future use)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Check if click is outside both the panel and the info button
      if (isInfoPanelOpen && 
          !target.closest('[data-profile-info-panel]') && 
          !target.closest('[data-profile-info-button]')) {
        setIsInfoPanelOpen(false);
      }
    };

    if (isInfoPanelOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isInfoPanelOpen]);

  const handleToggleInfoPanel = () => {
    setIsInfoPanelOpen(!isInfoPanelOpen);
  };

  const handleToggleGallery = () => {
    setIsGalleryOpen(!isGalleryOpen);
  };

  const showLoginChatPanel = async () => {
    console.log('Start chat clicked, session:', session);
    if (!session) {
      setLoginRedirectUrl(`/chat/${avatarId}`);
      setShowLoginPopup(true);
      return;
    }
    console.log('Starting video mode for avatar:', avatarId);
    // Explicitly call initiateVideoRoom once when button is clicked
    if (initiateVideoRoom) {
      await initiateVideoRoom();
    }
  };

  return (
    <>
      <div className="relative w-full lg:w-auto h-full max-h-full flex-shrink-0" data-unified-chat-panel>
        <div className="flex flex-col w-full h-full max-h-full overflow-hidden relative">
          
          {/* 1. Profile Header */}
          <ProfileHeader 
            onToggleInfoPanel={handleToggleInfoPanel}
            onToggleGallery={handleToggleGallery}
          />

          {/* 2. Chat Messages Container with Unified Controls */}
          <ChatMessagesContainer
            onStartChat={showLoginChatPanel}
            isGalleryOpen={isGalleryOpen}
            onToggleGallery={handleToggleGallery}
          />

        </div>
        
        {/* Login Popup */}
        <LoginPopup 
          isOpen={showLoginPopup} 
          onClose={() => {
            setShowLoginPopup(false);
          }}
          redirectTarget={loginRedirectUrl}
        />
      </div>
      
      {/* Audio Unlocker - always present when room exists */}
      <AudioUnlocker />
    </>
  );
}
